import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Gift, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralLandingPage = () => {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', course_interest: '', consent: false, website: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/training/public/refer/${encodeURIComponent(code || '')}`)
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.detail || "This referral link isn't valid");
        setInfo(body);
      })
      .catch(e => setError(e.message));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/training/public/refer/${encodeURIComponent(code)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) setDone(true);
      else setFormError(body.detail || 'Something went wrong. Please try again.');
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const discount = info?.friend_discount ? `£${Number(info.friend_discount).toLocaleString('en-GB')}` : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white px-4 py-10">
      <div className="max-w-md mx-auto">
        {!info && !error && (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-pink-400 animate-spin" /></div>
        )}

        {error && (
          <div className="text-center py-24">
            <p className="text-lg font-semibold text-slate-900">{error}</p>
            <p className="text-sm text-slate-500 mt-2">Ask your friend to send you their link again.</p>
          </div>
        )}

        {info && (
          <>
            <div className="text-center mb-6">
              {info.logo_url ? (
                <img src={info.logo_url} alt={info.centre} className="h-16 mx-auto mb-3 object-contain" />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-pink-100 text-pink-600 flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-7 h-7" />
                </div>
              )}
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {info.referrer_first_name} thinks you'd love training with {info.centre}
              </h1>
              {discount && (
                <p className="mt-2 text-slate-600">
                  Sign up through this page and get <span className="font-semibold text-pink-600">{discount} off</span> your course.
                </p>
              )}
            </div>

            {done ? (
              <div className="rounded-2xl bg-white border border-green-200 p-6 text-center shadow-sm">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-lg font-semibold text-slate-900">You're in!</p>
                <p className="text-sm text-slate-600 mt-1">{info.centre} will be in touch shortly to help you choose your course{discount ? ` and apply your ${discount} discount` : ''}.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-3">
                <div>
                  <label htmlFor="ref-name" className="text-sm font-medium text-slate-700 mb-1 block">Full name</label>
                  <Input id="ref-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required maxLength={80} autoComplete="name" className="h-11 rounded-xl" />
                </div>
                <div>
                  <label htmlFor="ref-phone" className="text-sm font-medium text-slate-700 mb-1 block">Mobile number</label>
                  <Input id="ref-phone" type="tel" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} maxLength={20} autoComplete="tel" className="h-11 rounded-xl" />
                </div>
                <div>
                  <label htmlFor="ref-email" className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                  <Input id="ref-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} maxLength={254} autoComplete="email" className="h-11 rounded-xl" />
                </div>
                {info.courses.length > 0 && (
                  <div>
                    <label htmlFor="ref-course" className="text-sm font-medium text-slate-700 mb-1 block">Which course are you interested in?</label>
                    <select id="ref-course" value={form.course_interest} onChange={(e) => setForm(p => ({ ...p, course_interest: e.target.value }))}
                      className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm">
                      <option value="">Not sure yet</option>
                      {info.courses.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                <input type="text" name="website" value={form.website} onChange={(e) => setForm(p => ({ ...p, website: e.target.value }))}
                  tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm(p => ({ ...p, consent: e.target.checked }))} className="mt-0.5 accent-pink-500" required />
                  I'm happy for {info.centre} to contact me by phone, text or email about their courses.
                </label>
                {formError && <p className="text-sm text-red-600">{formError}</p>}
                <Button type="submit" disabled={sending} className="w-full h-12 rounded-xl bg-gradient-to-r from-pink-400 to-rose-500 hover:from-pink-500 hover:to-rose-600 text-white font-medium">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {discount ? `Claim my ${discount} off` : 'Sign me up'}
                </Button>
                <p className="text-[11px] text-slate-400 text-center">Enter a mobile number or email so we can reach you.</p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralLandingPage;
